// packages/web-ui/src/components/SessionList/SessionRow.tsx
//
// W2-S8 — single session row in the ContextPanel Mode A list (UX §5.1
// lines 322–327).
//
// Per RB scope: this story ships a minimal Activate / Delete affordance.
// Pin / Archive / Rename / Export are out of scope for THIS narrowed slice
// (the parent prompt restricts the surface to "list + create + delete +
// resume"); those land in a follow-up story or are handled by parallel
// agents working on related lanes.

import type { JSX, KeyboardEvent } from 'react';
import type { SessionSummary } from '@tessa-lean/base/api-schemas/index.js';

export interface SessionRowProps {
  session: SessionSummary;
  isActive: boolean;
  onActivate: (sessionId: string) => void;
  onRequestDelete: (sessionId: string) => void;
}

/**
 * Render a relative timestamp from an ISO 8601 string. Pure function; the
 * row re-renders only when the parent re-renders (typical for session-list
 * updates). For real-time ticking the parent could wrap with `setInterval`
 * — out of scope for THIS row.
 */
export function formatRelativeTimestamp(value: string, now: Date = new Date()): string {
  const then = new Date(value);
  const ms = now.getTime() - then.getTime();
  if (Number.isNaN(ms)) return '';
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 24) return `${hours} h`;
  const days = Math.floor(ms / 86_400_000);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} d`;
  if (days < 30) return `${Math.floor(days / 7)} w`;
  // Month-day fallback
  return then.toISOString().slice(5, 10); // "MM-DD"
}

export function SessionRow({
  session,
  isActive,
  onActivate,
  onRequestDelete,
}: SessionRowProps): JSX.Element {
  const title = session.title && session.title.trim().length > 0 ? session.title : 'Untitled';
  const stamp = formatRelativeTimestamp(session.updatedAt);
  const ariaLabel = `${title}; ${session.messageCount} messages; updated ${stamp}`;

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onActivate(session.sessionId);
    } else if (e.key === 'Delete' || e.key === 'Backspace') {
      // Avoid accidental deletes — require modifier.
      if (e.shiftKey) {
        e.preventDefault();
        onRequestDelete(session.sessionId);
      }
    }
  }

  return (
    <li
      role="listitem"
      data-testid={`session-row-${session.sessionId}`}
      data-active={isActive ? 'true' : 'false'}
      className={`group relative flex flex-col gap-0.5 px-3 py-2 cursor-pointer outline-none focus:bg-cream-200 hover:bg-cream-200 ${
        isActive ? 'bg-cream-300' : ''
      }`}
    >
      <div
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
        aria-current={isActive ? 'page' : undefined}
        data-testid={`session-row-${session.sessionId}-body`}
        onClick={() => onActivate(session.sessionId)}
        onKeyDown={handleKeyDown}
        className="flex flex-col gap-0.5"
      >
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm text-taupe-900 truncate flex-1" data-testid={`session-row-${session.sessionId}-title`}>
            {title}
          </span>
          <span
            className="text-xs text-taupe-700 flex-shrink-0"
            data-testid={`session-row-${session.sessionId}-timestamp`}
          >
            {stamp}
          </span>
        </div>
        <div className="text-xs text-taupe-700">
          <span data-testid={`session-row-${session.sessionId}-count`}>
            {session.messageCount} {session.messageCount === 1 ? 'message' : 'messages'}
          </span>
        </div>
      </div>
      <button
        type="button"
        data-testid={`session-row-${session.sessionId}-delete`}
        aria-label={`Delete session ${title}`}
        onClick={(e) => {
          e.stopPropagation();
          onRequestDelete(session.sessionId);
        }}
        className="absolute right-2 top-2 rounded px-1 text-xs text-taupe-700 opacity-0 group-hover:opacity-100 focus:opacity-100 hover:text-accent-red"
      >
        ×
      </button>
    </li>
  );
}
